import React, { useState, useEffect } from 'react';
import { trackContactFormSubmitted, trackContactFormError } from '../utils/analytics';

interface SimpleDemoModalProps {
  isOpen: boolean;
  onClose: () => void;
  modalType?: 'demo' | 'trial' | 'quote';
}

const SimpleDemoModal: React.FC<SimpleDemoModalProps> = ({ isOpen, onClose, modalType = 'demo' }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formStartTime, setFormStartTime] = useState<number | null>(null);

  const getModalContent = () => {
    switch (modalType) {
      case 'trial':
        return {
          title: 'Start Free Trial',
          subtitle: 'Begin your 5-day free trial of Vertex Find',
          buttonText: 'Start Free Trial',
          successTitle: 'Free Trial Request Sent!',
          successMessage: 'We\'ll contact you shortly to set up your free trial account.'
        };
      case 'quote':
        return {
          title: 'Get a Quote',
          subtitle: 'Get custom pricing for your hiring needs',
          buttonText: 'Get Quote',
          successTitle: 'Quote Request Sent!',
          successMessage: 'We\'ll contact you shortly with a custom quote.'
        };
      default:
        return {
          title: 'Schedule a Demo',
          subtitle: 'Get a personalized demo of Vertex Find',
          buttonText: 'Schedule Demo',
          successTitle: 'Demo Request Sent!',
          successMessage: 'We\'ll contact you shortly to schedule your personalized demo.'
        };
    }
  };

  const modalContent = getModalContent();

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      // Track when form is opened
      setFormStartTime(Date.now());
    } else {
      document.body.style.overflow = 'unset';
      setFormStartTime(null);
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
    // Clear error when user starts typing
    if (error) {
      setError(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      // API endpoint for contact form
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001';
      const response = await fetch(`${apiBaseUrl}/api/public/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        // Calculate form completion time
        const completionTime = formStartTime ? Date.now() - formStartTime : null;
        
        // Track successful form submission
        trackContactFormSubmitted(modalType, {
          ...formData,
          completionTime: completionTime ? Math.round(completionTime / 1000) : null // in seconds
        });
        
        setIsSuccess(true);
        // Auto close after success
        setTimeout(() => {
          setIsSuccess(false);
          handleClose();
          setFormData({ name: '', email: '', message: '' });
        }, 3000);
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Server error: ${response.status}`);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      
      let errorMessage = 'An unexpected error occurred. Please try again.';
      if (error instanceof Error) {
        errorMessage = error.message;
        setError(error.message);
      } else {
        setError(errorMessage);
      }
      
      // Track form submission error
      trackContactFormError(modalType, errorMessage, formData);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  const handleClose = () => {
    setError(null);
    setIsSuccess(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fadeInUp"
      onClick={handleBackdropClick}
    >
      <div className="relative w-full max-w-md">
        <div className="p-0 rounded-2xl border-2 border-white/20 bg-black animate-fadeInUp transition-all duration-200 ease-out hover:border-white/30" style={{ animationDelay: '0.1s' }}>
          {/* Header */}
          <div className="relative bg-black p-6 rounded-t-2xl border-b border-white/20">
            <button
              onClick={handleClose}
              className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 transition-all duration-200 flex items-center justify-center text-gray-300 hover:text-white active:scale-95"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <div className="text-center">
              <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-purple-600 rounded-xl flex items-center justify-center text-xl mx-auto mb-4">
                📅
              </div>
              <h2 className="text-xl font-bold mb-2 text-white">
                {modalContent.title}
              </h2>
              <p className="text-gray-300 text-sm">
                {modalContent.subtitle}
              </p>
            </div>
          </div>

          {/* Form */}
          <div className="p-6">
            {!isSuccess ? (
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Name Field */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 focus:bg-white/10 transition-all duration-200 text-white placeholder-gray-400"
                    placeholder="Enter your full name"
                  />
                </div>

                {/* Email Field */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 focus:bg-white/10 transition-all duration-200 text-white placeholder-gray-400"
                    placeholder="you@company.com"
                  />
                </div>

                {/* Message Field */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Message (optional)
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={3}
                    className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 focus:bg-white/10 transition-all duration-200 resize-none text-white placeholder-gray-400"
                    placeholder="Tell us about your hiring needs..."
                  />
                </div>

                {/* Error Message */}
                {error && (
                  <div className="bg-red-900/30 border border-red-500/50 rounded-xl p-3 mb-4">
                    <div className="flex items-center gap-2">
                      <svg className="w-4 h-4 text-red-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <p className="text-sm text-red-300">{error}</p>
                    </div>
                  </div>
                )}

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full btn-primary text-base py-3 disabled:opacity-50 disabled:cursor-not-allowed mt-6 transition-all duration-200 hover:scale-105 active:scale-95"
                >
                  {isSubmitting ? (
                    <div className="flex items-center justify-center gap-2">
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                      Sending...
                    </div>
                  ) : (
                    <div className="flex items-center justify-center gap-2">
                      <span>{modalContent.buttonText}</span>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                      </svg>
                    </div>
                  )}
                </button>

                {/* Trust Indicator */}
                <div className="text-center pt-3">
                  <p className="text-xs text-gray-400">
                    ✓ No commitment required ✓ 30-minute demo
                  </p>
                </div>
              </form>
            ) : (
              /* Success State */
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center text-2xl mx-auto mb-4 animate-pulse">
                  ✅
                </div>
                <h3 className="text-lg font-semibold mb-2 text-green-400">
                  {modalContent.successTitle}
                </h3>
                <p className="text-gray-300 text-sm">
                  {modalContent.successMessage}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SimpleDemoModal;
